import * as React from 'react';
import clsx from 'clsx';
import { ModalShell } from '@/components/ui/ModalShell';
import copyIcon from '@/assets/images/svg/share_copy_icon.svg';
import closeIcon from '@/assets/images/svg/close_icon.svg';

interface ResponsiveShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  socials: Array<{ id: string; name: string; icon: string }>;
  onShare: (platform: string) => void;
  link: string;
  onCopy: () => void;
}

const SHARE_TITLE_GRADIENT_STYLE = {
  backgroundImage: 'linear-gradient(90deg, #1A1036 49.53%, #462C8E 95.55%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
} as const;

/** Cross-platform share entry point — bottom sheet on mobile, centered card on desktop. */
export const ResponsiveShareModal: React.FC<ResponsiveShareModalProps> = ({
  isOpen,
  onClose,
  socials,
  onShare,
  link,
  onCopy,
}) => (
  <ModalShell
    isOpen={isOpen}
    onClose={onClose}
    position='responsive-sheet'
    portal
    bodyClassName={clsx(
      'w-full bg-white',
      'rounded-t-20 sm:rounded-20 sm:max-w-[489px]',
      'flex max-h-[90vh] flex-col overflow-y-auto'
    )}
  >
    <div className='flex justify-end p-4'>
      <button onClick={onClose} className='transition-opacity hover:opacity-70' aria-label='Close'>
        <img src={closeIcon} alt='Close' className='size-6' />
      </button>
    </div>

    <div className='flex flex-col items-center px-6 pb-4'>
      <span
        className='text-center font-proxima text-24 font-bold leading-[47px]'
        style={SHARE_TITLE_GRADIENT_STYLE}
      >
        Share with Friends
      </span>
      <span className='text-center font-sans text-13 font-normal leading-21 text-gray-600'>
        Conversations are better when shared!
      </span>
    </div>

    <div className='flex justify-center gap-8 px-6 py-4'>
      {socials.map((social) => (
        <button
          key={social.id}
          onClick={() => onShare(social.id)}
          className='flex flex-col items-center gap-2'
        >
          <img src={social.icon} alt={social.name} className='size-[62px] object-contain' />
          <span className='font-sans text-11 font-medium leading-21 text-gray-670'>
            {social.name}
          </span>
        </button>
      ))}
    </div>

    <div className='mt-auto flex flex-col gap-2 bg-white px-6 pb-8 pt-4'>
      <span className='font-sans text-12 font-medium leading-21 text-dark-300'>Copy Message</span>

      <div className='flex w-full items-start gap-3 rounded-12 border border-purple-120 bg-white px-4 py-3'>
        <p className='min-w-0 flex-1 whitespace-pre-wrap break-words font-sans text-12 leading-18 text-brand-800'>
          {link}
        </p>
        <button
          onClick={onCopy}
          className='mt-0.5 shrink-0 transition-opacity hover:opacity-70'
          aria-label='Copy text'
        >
          <img src={copyIcon} alt='Copy' className='size-5' />
        </button>
      </div>
    </div>
  </ModalShell>
);
